/* eslint-disable max-lines */
import {
  Armchair, BadgePercent, BedDouble, Bell, BookUser, CalendarDays, CalendarRange,
  ChartColumn, CircleHelp, ClipboardList, Coffee, Compass, ConciergeBell, Contact, Croissant,
  DoorOpen, Flower2, History, Hotel, Images, Info, LayoutDashboard, LayoutGrid, ListChecks, MapPin,
  Megaphone, MessageSquareText, Package, Plus, Presentation, Puzzle, QrCode, ScrollText, Send,
  Settings, Shapes, Share2, ShoppingBag, ShoppingCart, Sparkles, Star, Store, Tag, ThumbsUp,
  TicketPercent, TrendingUp, Tv, Undo2, Users, Wifi, Wrench,
} from 'lucide-react';
import React from 'react';

import { OracleIcon, StripeIcon } from '../components/BrandIcons';
import type { MenuItemProps, SubitemProps } from '../types';
import { CUSTOM_BLOCKS_ENABLED } from './featureFlags';

// Menu w sekcjach (`kind: 'divider'`): Sprzedaż → Obsługa → Hotel i oferta →
// Usługi → Marketing i analityka → System. Kolejność idzie od tego, co recepcja
// otwiera codziennie, do konfiguracji odwiedzanej raz na jakiś czas. Wpisy `group`
// mają podstrony rozwijane w flyout-popoverze po najechaniu na rodzica.
const getBaseMenuConfig = (): MenuItemProps[] => [
  {
    kind: 'item',
    pathName: 'Dashboard',
    labelKey: 'menu.dashboard',
    path: '/',
    icon: <LayoutDashboard size={18} />,
  },

  /**
   * Sekcja SPRZEDAŻ — pieniądze i praca, którą one generują, w jednym miejscu.
   *
   * Stoi zaraz pod Dashboardem, bo recepcja zagląda tu najczęściej. Sekcja Opery jest
   * wstrzykiwana na indeks 1 (`ProtectedRoute`), więc PMS ląduje nad tym nagłówkiem.
   */
  { kind: 'divider', dividerLabel: 'Sprzedaż' },

  {
    kind: 'group',
    pathName: 'Płatności online',
    labelKey: 'menu.payments',
    path: '/payments',
    // Znak Stripe w fioletowym kwadracie, jak jego oficjalne logo.
    icon: <StripeIcon size={18} />,
    subItem: [
      {
        pathName: 'Transakcje',
        labelKey: 'menu.transactions',
        path: '/payments',
        icon: <TrendingUp size={18} />,
      },
      /* Zwroty osobno, bo to inna praca niż przeglądanie wpływów: lista zadań
         „oddaj pieniądze gościowi", zebrana ze wszystkich punktów naraz. */
      {
        pathName: 'Zwroty środków',
        labelKey: 'menu.refunds',
        path: '/payments/refunds',
        icon: <Undo2 size={18} />,
      },
    ],
  },

  /* Zamówienia, rezerwacje i zgłoszenia w jednej rozwijanej sekcji. Śniadania są
     doklejane warunkowo w `ProtectedRoute` (szuka grupy po ścieżce '/orders'). */
  {
    kind: 'group',
    pathName: 'Zamówienia i Zgłoszenia',
    labelKey: 'menu.ordersAndReports',
    path: '/orders',
    icon: <ShoppingBag size={18} />,
    subItem: [
      {
        pathName: 'Room Service',
        labelKey: 'menu.roomServiceOrders',
        path: '/orders',
        icon: <ConciergeBell size={18} />,
      },
      {
        pathName: 'Store',
        labelKey: 'menu.storePurchases',
        path: '/order-products',
        icon: <Package size={18} />,
      },
      {
        pathName: 'Rezerwacje Spa',
        labelKey: 'menu.wellness',
        path: '/reservations/wellness',
        icon: <Flower2 size={18} />,
      },
      {
        pathName: 'Rezerwacje wypożyczalni',
        labelKey: 'menu.rental',
        path: '/product-rental-reservations',
        icon: <ShoppingCart size={18} />,
      },
      {
        pathName: 'Zgłoszenia',
        labelKey: 'menu.reports',
        path: '/reports',
        icon: <Wrench size={18} />,
      },
    ],
  },
  {
    kind: 'group',
    pathName: 'Punkty sprzedaży',
    labelKey: 'menu.salesPoints',
    path: '/spa',
    icon: <Store size={18} />,
    subItem: [
      {
        pathName: 'Spa & Wellness',
        labelKey: 'menu.spa',
        path: '/spa',
        icon: <Flower2 size={18} />,
      },
      {
        pathName: 'Smart Room Service',
        labelKey: 'menu.roomService',
        path: '/take-to-room-service',
        icon: <ConciergeBell size={18} />,
      },
      {
        pathName: 'Serwis śniadaniowy',
        labelKey: 'menu.breakfastService',
        path: '/breakfast-service',
        icon: <Croissant size={18} />,
      },
      {
        pathName: 'Sklep',
        labelKey: 'menu.productsServices',
        path: '/products-services',
        icon: <ShoppingBag size={18} />,
      },
      {
        pathName: 'Wypożyczalnia Produktów',
        labelKey: 'menu.productRental',
        path: '/product-rental',
        icon: <ShoppingCart size={18} />,
      },
    ],
  },
  // Co gość widzi i co może u nas kupić — treść hotelu razem z katalogiem oferty.
  { kind: 'divider', dividerLabel: 'Hotel i oferta' },

  {
    kind: 'group',
    pathName: 'Mój Hotel',
    labelKey: 'menu.myHotel',
    path: '/hotel',
    icon: <Hotel size={18} />,
    subItem: [
      {
        pathName: 'Informacje hotelowe',
        labelKey: 'menu.hotelInformation',
        path: '/hotel/general-information',
        icon: <Info size={18} />,
      },
      {
        pathName: 'Udogodnienia',
        labelKey: 'menu.amenities',
        path: '/hotel/amenities',
        icon: <Armchair size={18} />,
      },
      {
        pathName: 'Wizytówki',
        labelKey: 'menu.businessCards',
        path: '/hotel/business-card',
        icon: <Contact size={18} />,
      },
      {
        pathName: 'Dobre praktyki',
        labelKey: 'menu.goodPractices',
        path: '/hotel/good-practice',
        icon: <ThumbsUp size={18} />,
      },
      {
        pathName: 'Książka telefoniczna',
        labelKey: 'menu.phoneBook',
        path: '/hotel/phonebook',
        icon: <BookUser size={18} />,
      },
      {
        pathName: 'Wifi',
        labelKey: 'menu.wifi',
        path: '/hotel/wifi',
        icon: <Wifi size={18} />,
      },
      {
        pathName: 'Regulaminy',
        labelKey: 'menu.regulations',
        path: '/hotel/regulations',
        icon: <ScrollText size={18} />,
      },
      {
        pathName: 'Poi',
        labelKey: 'menu.poi',
        path: '/hotel/poi',
        icon: <MapPin size={18} />,
      },
      {
        pathName: 'Kanały TV',
        labelKey: 'menu.tvChannels',
        path: '/hotel/tv-chanel',
        icon: <Tv size={18} />,
      },
      {
        pathName: 'Faq',
        labelKey: 'menu.faq',
        path: '/hotel/faq',
        icon: <CircleHelp size={18} />,
      },
      {
        pathName: 'Program Lojalnościowy',
        labelKey: 'menu.loyaltyProgram',
        path: '/hotel/loyalty-program',
        icon: <Star size={18} />,
      },
      {
        pathName: 'SocialMedia',
        labelKey: 'menu.socialMedia',
        path: '/hotel/social-media',
        icon: <Share2 size={18} />,
      },
      {
        pathName: 'Wirtualny spacer',
        labelKey: 'menu.virtualTour',
        path: '/hotel/virtual-tour',
        icon: <Compass size={18} />,
      },
      {
        pathName: 'Linki do recenzji',
        labelKey: 'menu.reviewLinks',
        path: '/hotel/link-to-reviews',
        icon: <MessageSquareText size={18} />,
      },
      // Ukryte flagą CUSTOM_BLOCKS_ENABLED (featureFlags.ts).
      ...(CUSTOM_BLOCKS_ENABLED
        ? [{
          pathName: 'Bloki niestandardowe',
          labelKey: 'menu.customBlocks',
          path: '/hotel/custom-blocks',
          icon: <Shapes size={18} />,
        }]
        : []),
    ],
  },

  {
    kind: 'item',
    pathName: 'Generator QR',
    labelKey: 'menu.qrGenerator',
    path: '/qr-generator',
    icon: <QrCode size={18} />,
  },

  {
    kind: 'group',
    pathName: 'Oferty Specjalne',
    labelKey: 'menu.specialOffers',
    path: '/special-offer',
    icon: <BadgePercent size={18} />,
    subItem: [
      {
        pathName: 'Utwórz ofertę',
        labelKey: 'menu.create',
        path: '/special-offer/new',
        icon: <Plus size={18} />,
      },
      {
        pathName: 'Wszystkie',
        labelKey: 'menu.all',
        path: '/special-offer',
        icon: <ListChecks size={18} />,
      },
      {
        pathName: 'Kategorie',
        labelKey: 'menu.categories',
        path: '/special-offer/categories',
        icon: <Tag size={18} />,
      },
    ],
  },

  {
    kind: 'group',
    pathName: 'Wydarzenia',
    labelKey: 'menu.events',
    path: '/events',
    icon: <CalendarDays size={18} />,
    subItem: [
      {
        pathName: 'Utwórz wydarzenie',
        labelKey: 'menu.create',
        path: '/events/new',
        icon: <Plus size={18} />,
      },
      {
        pathName: 'Wszystkie',
        labelKey: 'menu.all',
        path: '/events/all-events',
        icon: <CalendarRange size={18} />,
      },
      {
        pathName: 'Kategorie',
        labelKey: 'menu.categories',
        path: '/events/categories',
        icon: <Tag size={18} />,
      },
      {
        pathName: 'Lista rezerwacji',
        labelKey: 'menu.reservationList',
        path: '/events/register',
        icon: <ListChecks size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Konferencje',
    labelKey: 'menu.conferences',
    path: '/conferences',
    icon: <Presentation size={18} />,
  },

  /* Sekcja USŁUGI — punkty sprzedaży i widgety, czyli konfiguracja tego, co gość
     może u nas zamówić. Osobno od „Hotel i oferta": tam jest treść o obiekcie,
     tu narzędzia, które trzeba skonfigurować, zanim ruszy sprzedaż. */
  { kind: 'divider', dividerLabel: 'Usługi' },

  {
    kind: 'item',
    pathName: 'Inne',
    labelKey: 'menu.other',
    path: '/widgets',
    icon: <LayoutGrid size={18} />,
  },

  // Dotarcie do gościa i sprawdzenie, czy zadziałało — promocja obok liczb.
  { kind: 'divider', dividerLabel: 'Marketing i analityka' },

  {
    kind: 'group',
    pathName: 'Marketing',
    labelKey: 'menu.marketing',
    path: '/alerts',
    icon: <Megaphone size={18} />,
    subItem: [
      {
        pathName: 'Powiadomienia',
        labelKey: 'menu.notifications',
        path: '/alerts',
        icon: <Bell size={18} />,
      },
      {
        pathName: 'Kody rabatowe',
        labelKey: 'menu.promoCodes',
        path: '/promo-codes',
        icon: <TicketPercent size={18} />,
      },
      {
        pathName: 'Kampanie Push',
        labelKey: 'menu.pushCampaigns',
        path: '/marketing',
        icon: <Send size={18} />,
      },
      {
        pathName: 'Subskrybenci',
        labelKey: 'menu.subscribers',
        path: '/subscribers',
        icon: <Users size={18} />,
      },
    ],
  },

  {
    kind: 'item',
    pathName: 'Ankiety',
    labelKey: 'menu.surveys',
    path: '/surveys',
    icon: <ClipboardList size={18} />,
  },

  // Analityka, analiza sprzedaży i dziennik aktywności w jednej rozwijanej sekcji.
  {
    kind: 'group',
    pathName: 'Analityka',
    labelKey: 'menu.analytics',
    path: '/analytics',
    icon: <ChartColumn size={18} />,
    subItem: [
      {
        pathName: 'Analityka',
        labelKey: 'menu.analytics',
        path: '/analytics',
        icon: <ChartColumn size={18} />,
      },
      {
        pathName: 'Analiza Sprzedaży',
        labelKey: 'menu.salesAnalytics',
        path: '/sales-analytics',
        icon: <TrendingUp size={18} />,
      },
      {
        pathName: 'Dziennik aktywności',
        labelKey: 'menu.activityJournal',
        path: '/activity-journal',
        icon: <History size={18} />,
      },
    ],
  },

  // Konfiguracja, do której wchodzi się raz na jakiś czas.
  { kind: 'divider', dividerLabel: 'System' },

  {
    kind: 'item',
    pathName: 'AI Agents',
    labelKey: 'menu.aiAgents',
    path: '/ai-agents',
    icon: <Sparkles size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Biblioteka mediów',
    labelKey: 'menu.mediaLibrary',
    path: '/media-library',
    icon: <Images size={18} />,
  },

  {
    kind: 'item',
    pathName: 'Ustawienia',
    labelKey: 'menu.settings',
    path: '/settings',
    icon: <Settings size={18} />,
  },
];

const getDividerLabel = (label: string | undefined, t: (key: string) => string): string => {
  if (!label) return '';
  const labelMap: Record<string, string> = {
    Zarządzanie: t('sidebar.management'),
    Hotel: t('sidebar.hotel'),
    Sprzedaż: t('sidebar.sales'),
    Obsługa: t('sidebar.guestService'),
    'Hotel i oferta': t('sidebar.hotelAndOffer'),
    'Marketing i analityka': t('sidebar.marketingAndAnalytics'),
    System: t('sidebar.system'),
    Marketing: t('sidebar.marketing'),
    'Wydarzenia i Rezerwacje': t('sidebar.eventsAndReservations'),
    Usługi: t('sidebar.services'),
    'Analityka i Feedback': t('sidebar.analyticsAndFeedback'),
    Pozostałe: t('sidebar.other'),
    Muzeum: t('sidebar.museum'),
    'Aplikacje zewnętrzne': t('sidebar.externalApplications'),
  };
  return labelMap[label] || label;
};

export const getTranslatedMenuConfig = (t: (key: string) => string): MenuItemProps[] => {
  const baseConfig = getBaseMenuConfig();
  return baseConfig.map((item) => {
    if (item.kind === 'divider') {
      return {
        ...item,
        dividerLabel: getDividerLabel(item.dividerLabel, t),
      };
    }
    if (item.kind === 'item') {
      const translations: Record<string, string> = {
        Dashboard: t('sidebar.dashboard'),
        Zgłoszenia: t('sidebar.reports'),
        Zamówienia: t('sidebar.orders'),
        Zakupy: t('sidebar.shopping'),
        Rezerwacje: t('sidebar.reservations'),
        'Oferty Specjalne': t('sidebar.specialOffers'),
        Powiadomienia: t('sidebar.notifications'),
        'Kody rabatowe': t('sidebar.promoCodes'),
        Konferencje: t('sidebar.conferences'),
        'Spa & Wellness': t('sidebar.spa'),
        Ankiety: t('sidebar.surveys'),
        Analityka: t('sidebar.analytics'),
        'Analiza Sprzedaży': t('sidebar.salesAnalytics'),
        'Płatności online': t('sidebar.payments'),
        'Dziennik aktywności': t('sidebar.activityJournal'),
        'Biblioteka mediów': t('sidebar.mediaLibrary'),
        Ustawienia: t('sidebar.settings'),
        'Kampanie Push': t('sidebar.clickAndPush'),
        Subskrybenci: t('sidebar.subscribers'),
        'Smart Room Service': t('sidebar.smartRoomService'),
        Sklep: t('sidebar.productsServices'),
        'Wypożyczalnia Produktów': t('sidebar.productRental'),
        'AI Agent Dashboard': t('sidebar.aiAgentDashboard'),
        'AI Agents': t('sidebar.aiAgents'),
        Inne: t('sidebar.other'),
        'Generator QR': t('sidebar.qrGenerator'),
        'Moje urządzenia': t('sidebar.myDevices'),
        Museum: t('sidebar.museum'),
      };
      return {
        ...item,
        pathName: translations[item.pathName] || item.pathName,
      };
    }
    if (item.kind === 'group') {
      const groupTranslations: Record<string, string> = {
        Zarządzanie: t('sidebar.management'),
        'Mój Hotel': t('sidebar.myHotel'),
        Wydarzenia: t('sidebar.events'),
        'Oferty Specjalne': t('sidebar.specialOffers'),
        Marketing: t('sidebar.marketing'),
        Usługi: t('sidebar.services'),
        'Punkty sprzedaży': t('sidebar.salesPoints'),
        Wypożyczalnia: t('sidebar.rental'),
        'Zamówienia i Zgłoszenia': t('sidebar.ordersAndReports'),
        'Płatności online': t('sidebar.payments'),
        Rezerwacje: t('sidebar.reservations'),
        Analityka: t('sidebar.analytics'),
      };
      const subItemTranslations: Record<string, string> = {
        // Marketing — te cztery są subitemami grupy, więc mapa dla `kind: 'item'`
        // ich nie obejmowała i zostawały po polsku niezależnie od języka.
        Powiadomienia: t('sidebar.notifications'),
        'Kody rabatowe': t('sidebar.promoCodes'),
        'Kampanie Push': t('sidebar.clickAndPush'),
        Subskrybenci: t('sidebar.subscribers'),
        'Informacje hotelowe': t('sidebar.hotelInformation'),
        Udogodnienia: t('sidebar.amenities'),
        Wizytówki: t('sidebar.businessCards'),
        'Dobre praktyki': t('sidebar.goodPractices'),
        'Książka telefoniczna': t('sidebar.phoneBook'),
        Wifi: t('sidebar.wifi'),
        Regulaminy: t('sidebar.regulations'),
        Poi: t('sidebar.poi'),
        'Kanały TV': t('sidebar.tvChannels'),
        Faq: t('sidebar.faq'),
        'Program Lojalnościowy': t('sidebar.loyaltyProgram'),
        SocialMedia: t('sidebar.socialMedia'),
        'Wirtualny spacer': t('sidebar.virtualTour'),
        'Linki do recenzji': t('sidebar.reviewLinks'),
        'Bloki niestandardowe': t('sidebar.customBlocks'),
        'Utwórz wydarzenie': t('sidebar.createEvent'),
        Wszystkie: t('sidebar.all'),
        Kategorie: t('sidebar.categories'),
        'Lista ofert': t('sidebar.offerList'),
        'Utwórz ofertę': t('sidebar.addOffer'),
        'Dodaj kategorię': t('sidebar.addCategory'),
        'Lista rezerwacji': t('sidebar.reservationList'),
        Produkty: t('sidebar.products'),
        Rezerwacje: t('sidebar.rental'),
        'Room Service': t('sidebar.ordersRoomService'),
        Store: t('sidebar.shoppingStore'),
        'Lista produktów': t('sidebar.productList'),
        // Outlety — subitemy grupy „Usługi", więc mapa dla `kind: 'item'`
        // ich nie obejmowała i zostawały po polsku/EN niezależnie od języka.
        'Spa & Wellness': t('sidebar.spa'),
        'Smart Room Service': t('sidebar.smartRoomService'),
        'Serwis śniadaniowy': t('sidebar.breakfastService'),
        Sklep: t('sidebar.productsServices'),
        'Wypożyczalnia Produktów': t('sidebar.productRental'),
        Transakcje: t('sidebar.transactions'),
        'Zwroty środków': t('sidebar.refunds'),
        Analityka: t('sidebar.analytics'),
        'Analiza Sprzedaży': t('sidebar.salesAnalytics'),
        'Dziennik aktywności': t('sidebar.activityJournal'),
        'Rezerwacje Spa': t('sidebar.spaReservations'),
        'Rezerwacje wypożyczalni': t('sidebar.rentalReservations'),
        Zgłoszenia: t('sidebar.reports'),
      };
      return {
        ...item,
        pathName: groupTranslations[item.pathName] || item.pathName,
        subItem: item.subItem?.map((sub) => ({
          ...sub,
          pathName: subItemTranslations[sub.pathName] || sub.pathName,
        })),
      };
    }
    return item;
  });
};

export const MenuConfig: MenuItemProps[] = getBaseMenuConfig();

/* Lista śniadaniowa wisi w zamówieniach, nie przy konfiguracji serwisu: to
   widok operacyjny (kuchnia patrzy na nią rano), a nie ustawienia outletu.
   Wstrzykiwana warunkowo — hotel bez serwisu śniadaniowego nie ma tu czego
   otwierać (patrz `ProtectedRoute`). */
export const getBreakfastOrdersEntry = (t: (key: string) => string): SubitemProps => ({
  pathName: t('sidebar.breakfastOrders'),
  path: '/breakfast-orders',
  icon: <Croissant size={18} />,
});

export const getTranslatedMuseumEntries = (t: (key: string) => string): MenuItemProps[] => [
  { kind: 'divider', dividerLabel: t('sidebar.museum') },
  {
    kind: 'item',
    pathName: t('sidebar.museum'),
    path: '/museum',
    icon: <LayoutDashboard size={18} />,
  },
];

export const museumEntries: MenuItemProps[] = [
  { kind: 'divider', dividerLabel: 'Muzeum' },
  {
    kind: 'item',
    pathName: 'Museum',
    labelKey: 'menu.museumPage',
    path: '/museum',
    icon: <LayoutDashboard size={18} />,
  },
];

/**
 * Sekcja PMS — doklejana tylko hotelom z włączoną integracją (patrz `ProtectedRoute`).
 * Dane pochodzą na żywo z systemu hotelowego, więc trzymamy ją osobno od naszych treści.
 */
export const getTranslatedOperaEntries = (t: (key: string) => string): MenuItemProps[] => [
  {
    kind: 'group',
    pathName: t('sidebar.opera'),
    path: '/opera',
    icon: <OracleIcon size={18} />,
    subItem: [
      {
        pathName: t('sidebar.operaArrivals'),
        path: '/opera/arrivals',
        icon: <DoorOpen size={18} />,
      },
      {
        pathName: t('sidebar.operaInHouseGuests'),
        path: '/opera/in-house',
        icon: <Users size={18} />,
      },
      {
        pathName: t('sidebar.operaRooms'),
        path: '/opera/rooms',
        icon: <BedDouble size={18} />,
      },
      {
        pathName: t('sidebar.operaBreakfast'),
        path: '/opera/breakfast',
        icon: <Coffee size={18} />,
      },
      {
        pathName: t('sidebar.operaBreakfastStats'),
        path: '/opera/breakfast-stats',
        icon: <ChartColumn size={18} />,
      },
    ],
  },
];

// External Applications menu entries
export const getTranslatedExternalAppsEntries = (t: (key: string) => string): MenuItemProps[] => [
  { kind: 'divider', dividerLabel: t('sidebar.externalApplications') },
  {
    kind: 'item',
    pathName: t('sidebar.museum'),
    path: '/museum',
    icon: <Puzzle size={18} />,
  },
];
